import React, { useEffect, useState } from "react";
import {
    Button,
    Grid,
    Paper,
    Stack,
    Typography,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    IconButton,
    Tooltip,
    TextField,
    Card,
} from "@mui/material";
import {
    FilterList as FilterListIcon,
    Clear as ClearIcon,
    Refresh as RefreshIcon,
    GetApp as ExportIcon,
    Security as SecurityIcon,
} from "@mui/icons-material";

import InsuranceTableComponent from "../../../components/admin/insurance-manager/InsuranceTable";
import InsuranceDetailsDialog from "../../../components/admin/insurance-manager/InsuranceDetailsDialog";

import {
    useGetAdminInsuranceRenewalsQuery,
    useDeleteInsuranceRenewalMutation,
} from "../../../store/insurance/insuranceApis";
import { defaultPaginate } from "../../../helpers/constants";
import { useGetProductsDropdownQuery } from "../../../store/masters/mastersApis";
import { SearchRecords } from "../../../components/Common/SearchRecords";

const InsuranceContainer = (props) => {
    const { showDrawer, formType, initialValues, pagination, filters, selectedInsurance, showInsuranceDetails } = props;

    const [showFilters, setShowFilters] = useState(true);
    const [selectedInsuranceForDetails, setSelectedInsuranceForDetails] = useState(null);

    // Build query parameters
    const queryParams = {
        page: pagination.pageNo + 1,
        limit: pagination.limit,
        sort_by: pagination.sortField,
        sort_order: pagination.sortBy,
        search: pagination.keywords,
        // Only include non-null filters
        ...(filters.product_id && filters.product_id !== "null" && { product_id: filters.product_id }),
        ...(filters.start_date && filters.start_date !== "null" && { start_date: filters.start_date }),
        ...(filters.end_date && filters.end_date !== "null" && { end_date: filters.end_date }),
    };

    const { isLoading, data: insuranceRenewals, isFetching, refetch } = useGetAdminInsuranceRenewalsQuery(queryParams);
    const { data: productsData } = useGetProductsDropdownQuery();
    const [deleteInsuranceRenewal] = useDeleteInsuranceRenewalMutation();

    useEffect(() => {
        props.handlePaginationState(defaultPaginate);
    }, []);

    const handlePagination = (setting) => {
        props.handlePaginationState(setting);
    };

    const handleFilterChange = (filterName, value) => {
        props.updateFilters({
            [filterName]: value,
        });
        props.handlePaginationState({
            ...pagination,
            pageNo: 0,
        });
    };

    const handleClearFilters = () => {
        props.resetFilters();
        props.handlePaginationState({
            ...pagination,
            keywords: "",
            pageNo: 0,
        });
    };

    const handleViewDetails = (insurance) => {
        console.log("handleViewDetails called with insurance:", insurance);
        setSelectedInsuranceForDetails(insurance);
        props.setShowInsuranceDetails(true);
    };

    const handleDelete = async (insurance) => {
        console.log("Delete insurance:", insurance);
        try {
            await deleteInsuranceRenewal(insurance.id).unwrap();
            refetch(); // Refresh the list after successful deletion
        } catch (error) {
            console.error("Error deleting insurance renewal:", error);
        }
    };

    const handleCloseDetails = () => {
        setSelectedInsuranceForDetails(null);
        props.setShowInsuranceDetails(false);
    };

    const getActiveFiltersCount = () => {
        let count = 0;
        if (filters.product_id) count++;
        if (filters.start_date) count++;
        if (filters.end_date) count++;
        return count;
    };

    // Calculate payment status counts for dashboard cards
    const statusCounts = insuranceRenewals?.results?.reduce(
        (acc, insurance) => {
            acc[insurance.payment_status] = (acc[insurance.payment_status] || 0) + 1;
            return acc;
        },
        { pending: 0, paid: 0, failed: 0, cancelled: 0 },
    ) || { pending: 0, paid: 0, failed: 0, cancelled: 0 };

    return (
        <Stack spacing={2}>
            {/* Header */}
            <Grid container justifyContent="space-between" alignItems="center">
                <Grid item xs={6} sx={{ alignSelf: "center" }}>
                    <Typography variant="h6">
                        Insurance Renewals
                        <span style={{ color: "gray", fontSize: "10px", verticalAlign: "super", marginLeft: 8 }}>
                            ({insuranceRenewals?.count || 0})
                        </span>
                    </Typography>
                </Grid>
                <Grid
                    item
                    xs={6}
                    sx={{ alignSelf: "center" }}
                    display={"flex"}
                    flexDirection={"row"}
                    justifyContent={"flex-end"}
                >
                    <SearchRecords handlePagination={handlePagination} pagination={pagination} />
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 2 }}>
                        <Tooltip title="Refresh">
                            <IconButton onClick={() => refetch()} size="small">
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Export">
                            <IconButton size="small">
                                <ExportIcon />
                            </IconButton>
                        </Tooltip>
                        <Button
                            variant="outlined"
                            startIcon={<FilterListIcon />}
                            onClick={() => setShowFilters(!showFilters)}
                            size="small"
                        >
                            Filters
                            {getActiveFiltersCount() > 0 && (
                                <Chip
                                    label={getActiveFiltersCount()}
                                    size="small"
                                    sx={{
                                        ml: 1,
                                        height: 20,
                                        fontSize: "0.7rem",
                                        backgroundColor: "#1976d2",
                                        color: "white",
                                    }}
                                />
                            )}
                        </Button>
                    </Stack>
                </Grid>
            </Grid>

            {/* Payment Status Dashboard Cards */}
            <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                    <Card variant="outlined" sx={{ p: 2 }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    Pending
                                </Typography>
                                <Typography variant="h5" fontWeight="bold" color="warning.main">
                                    {statusCounts.pending}
                                </Typography>
                            </Box>
                            <SecurityIcon sx={{ fontSize: 32, color: "warning.main" }} />
                        </Box>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Card variant="outlined" sx={{ p: 2 }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    Paid
                                </Typography>
                                <Typography variant="h5" fontWeight="bold" color="success.main">
                                    {statusCounts.paid}
                                </Typography>
                            </Box>
                            <SecurityIcon sx={{ fontSize: 32, color: "success.main" }} />
                        </Box>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Card variant="outlined" sx={{ p: 2 }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    Failed
                                </Typography>
                                <Typography variant="h5" fontWeight="bold" color="error.main">
                                    {statusCounts.failed}
                                </Typography>
                            </Box>
                            <SecurityIcon sx={{ fontSize: 32, color: "error.main" }} />
                        </Box>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Card variant="outlined" sx={{ p: 2 }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    Cancelled
                                </Typography>
                                <Typography variant="h5" fontWeight="bold" color="grey.600">
                                    {statusCounts.cancelled}
                                </Typography>
                            </Box>
                            <SecurityIcon sx={{ fontSize: 32, color: "grey.600" }} />
                        </Box>
                    </Card>
                </Grid>
            </Grid>

            {/* Filters */}
            {showFilters && (
                <Paper sx={{ p: 3, mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                        Filters
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Product</InputLabel>
                                <Select
                                    value={filters.product_id || ""}
                                    onChange={(e) => handleFilterChange("product_id", e.target.value || null)}
                                    label="Product"
                                >
                                    <MenuItem value="">All Products</MenuItem>
                                    {productsData?.map((product) => (
                                        <MenuItem key={product.value} value={product.value}>
                                            {product.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label="Start Date"
                                value={filters.start_date || ""}
                                onChange={(e) => handleFilterChange("start_date", e.target.value || null)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ height: "100%" }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="date"
                                    label="End Date"
                                    value={filters.end_date || ""}
                                    onChange={(e) => handleFilterChange("end_date", e.target.value || null)}
                                    InputLabelProps={{ shrink: true }}
                                />
                                <Button
                                    variant="outlined"
                                    startIcon={<ClearIcon />}
                                    onClick={handleClearFilters}
                                    size="small"
                                    disabled={getActiveFiltersCount() === 0}
                                >
                                    Clear
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {/* Insurance Table */}
            <Grid container>
                <Grid item xs={12}>
                    <InsuranceTableComponent
                        loading={isLoading}
                        fetching={isFetching}
                        count={insuranceRenewals?.count}
                        data={insuranceRenewals?.results || []}
                        onViewDetails={handleViewDetails}
                        onDelete={handleDelete}
                        pagination={pagination}
                        handlePagination={handlePagination}
                    />
                </Grid>
            </Grid>

            {/* Insurance Details Dialog */}
            <InsuranceDetailsDialog
                open={showInsuranceDetails}
                onClose={handleCloseDetails}
                insuranceId={selectedInsuranceForDetails?.id}
            />
        </Stack>
    );
};

export default InsuranceContainer;
